<template>
  <div>
    <b-alert
      v-if="!resolvedExternalRequestId && !attachmentsSectionLoading"
      variant="warning"
      show
      class="mb-0"
    >
      معرف الطلب الخارجي غير متوفر.
    </b-alert>
    <div
      v-else-if="attachmentsSectionLoading"
      class="attachments-144-loader d-flex flex-column align-items-center justify-content-center py-4"
    >
      <b-spinner
        variant="primary"
        label="جاري التحميل"
      />
      <span class="text-muted small mt-2">جاري تحميل المرفقات...</span>
    </div>
    <div
      v-else-if="attachmentFields.length"
      class="d-flex flex-wrap required-block-cards"
    >
      <div
        v-for="group in attachmentGroups"
        :key="group.groupKey"
        class="w-100 mb-1 attachment-field-group"
        :class="attachmentGroupAccentClass(group)"
        :style="attachmentGroupCssVars(group)"
      >
        <b-button
          variant="outline-secondary"
          class="w-100 d-flex align-items-center justify-content-between mb-1 attachment-field-group__toggle"
          @click="toggleGroupCollapse(group.groupKey)"
        >
          <span>{{ group.groupTitle }}</span>
          <feather-icon :icon="isGroupExpanded(group.groupKey) ? 'ChevronUpIcon' : 'ChevronDownIcon'" />
        </b-button>

        <b-collapse :visible="isGroupExpanded(group.groupKey)">
          <div class="d-flex flex-wrap required-block-cards">
            <b-card
              v-for="field in group.fields"
              :key="field.key"
              class="media-card mb-1 shadow-sm"
              body-class="p-1 d-flex flex-column align-items-center"
              style="width: 200px; min-height: 300px;"
            >
              <div class="w-100 text-center">
                <div
                  class="file-title font-weight-bold"
                  style="word-break: break-word;"
                >
                  {{ field.label }}
                </div>
                <div
                  v-if="isFieldMultiple(field)"
                  class="text-muted small"
                >
                  متعدد
                </div>
              </div>
              <div class="media-file-details w-100 text-center">
                <template v-if="getConditionalFiles(field.key).length">
                  <div
                    v-for="file in getConditionalFiles(field.key)"
                    :key="file.id || file.url || file.name"
                    class="mb-1"
                  >
                    <div class="file-meta text-muted small">
                      {{ (file.extension || '').toUpperCase() }} • {{ file.human_readable_size }}
                    </div>
                    <div class="file-meta text-muted small">
                      مرفوع بواسطة<br>{{ file.uploader ? file.uploader.name : '' }}
                    </div>
                    <div class="file-meta text-muted small">
                      تاريخ الرفع<br>{{ file.created_at | formatJalali }}
                    </div>
                  </div>
                </template>
                <template v-else>
                  <div class="text-muted small">
                    غير موجود
                  </div>
                </template>
              </div>

              <div class="w-100 mt-1">
                <b-form-file
                  v-model="fieldUploads[field.key]"
                  :state="hasSelectedFiles(field)"
                  :placeholder="isFieldMultiple(field) ? 'اختر ملفات' : 'اختر ملف'"
                  drop-placeholder="اسحب الملف هنا"
                  browse-text="تصفح"
                  :accept="fieldAccept(field)"
                  :multiple="isFieldMultiple(field)"
                />
                <b-button
                  class="mt-1 w-100"
                  size="sm"
                  variant="primary"
                  :disabled="!hasSelectedFiles(field) || isUploadingConditional(field.key)"
                  @click="submitFieldUpload(field)"
                >
                  <b-spinner
                    v-if="isUploadingConditional(field.key)"
                    small
                    class="mr-1"
                  />
                  {{ getConditionalFile(field.key) ? (isFieldMultiple(field) ? 'إضافة' : 'تحديث') : 'رفع' }}
                </b-button>
              </div>
              <div
                v-for="file in getConditionalFiles(field.key)"
                :key="`actions-${field.key}-${file.id || file.url}`"
                class="media-card-actions d-flex flex-column flex-md-row justify-content-center gap-1 w-100 mt-1"
              >
                <b-button
                  v-if="canPreviewFile(file)"
                  :href="file.url"
                  target="_blank"
                  size="sm"
                  variant="info"
                  @click="handleFileActionRefresh(field.key)"
                >
                  معاينة
                </b-button>
                <b-button
                  :href="file.download_url || file.url"
                  target="_blank"
                  size="sm"
                  variant="success"
                  @click="handleFileActionRefresh(field.key)"
                >
                  تحميل
                </b-button>
                <b-button
                  size="sm"
                  variant="danger"
                  @click="deleteConditionalFile(field.key, file.id)"
                >
                  حذف
                </b-button>
              </div>
            </b-card>
          </div>
        </b-collapse>
      </div>
    </div>
    <b-alert
      v-else-if="resolvedExternalRequestId && !attachmentsSectionLoading"
      variant="info"
      show
      class="mb-0"
    >
      لا توجد مرفقات مطلوبة لهذا النوع من الخدمة.
    </b-alert>
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex'
import externalRequestsApi from '@/api/external-requests/externalRequests'
import lookupAttachmentUpload from '@/mixins/lookupAttachmentUpload'
import {
  BCard,
  BButton,
  BFormFile,
  BSpinner,
  BAlert,
  BCollapse,
} from 'bootstrap-vue'

const SERVICE_TYPE_ID = 14

export default {
  name: 'Attachments144',
  mixins: [lookupAttachmentUpload],
  components: {
    BCard,
    BButton,
    BFormFile,
    BSpinner,
    BAlert,
    BCollapse,
  },
  props: {
    externalRequestId: {
      type: [Number, String],
      default: null,
    },
  },
  data() {
    return {
      media: {},
      mediaLoading: false,
      lastLoadedServiceTypeId: null,
      fieldUploads: {},
      uploadingFields: {},
      localUploadedFiles: {},
      localDeletedFiles: {},
      localDeletedFileIds: {},
      attachmentGroupCollapse: {},
    }
  },
  computed: {
    ...mapGetters('attachmentLookups', {
      attachmentLookupsLoading: 'load',
    }),
    resolvedExternalRequestId() {
      const id = Number(this.externalRequestId)
      return Number.isInteger(id) && id > 0 ? id : null
    },
    attachmentsSectionLoading() {
      if (!this.resolvedExternalRequestId) {
        return this.attachmentLookupsLoading
      }
      return !!(this.mediaLoading || this.attachmentLookupsLoading)
    },
    allFiles() {
      if (!this.media || typeof this.media !== 'object') {
        return []
      }
      const allFilesArray = []
      for (const groupKey in this.media) {
        if (Array.isArray(this.media[groupKey])) {
          allFilesArray.push(
            ...this.media[groupKey].map(f => ({
              ...f,
              groupKey,
            })),
          )
        }
      }
      return allFilesArray
    },
    ...mapState('attachmentLookups', ['serviceTypesAttachmentLookups']),
    attachmentFields() {
      const lookups = this.serviceTypesAttachmentLookups || []
      const fields = []

      lookups.forEach((entry, sectionIndex) => {
        if (entry && Array.isArray(entry.attachment_lookups)) {
          const groupTitle = entry.title != null ? String(entry.title) : 'عام'
          const groupKey = `section_${sectionIndex}_${this.normalizeName(groupTitle)}`
          const sectionSortOrder = this.pickGroupSortOrder(entry, sectionIndex)
          const sectionColor = this.pickGroupColor(entry)
          const items = [...entry.attachment_lookups].sort((a, b) => {
            const so = (a?.sort_order ?? 0) - (b?.sort_order ?? 0)
            if (so !== 0) return so
            return (a?.id ?? 0) - (b?.id ?? 0)
          })
          items.forEach((lookup, idx) => {
            if (!lookup || lookup.is_active === false) return
            const idPart = lookup.id != null ? lookup.id : idx
            const fieldKey = `lookup_${idPart}`
            const label =
              this.normalizeName(lookup.key)
              || this.normalizeName(lookup.name_ar)
              || this.normalizeName(lookup.name)
              || `attachment_${idPart}`
            fields.push({
              key: fieldKey,
              label,
              groupTitle,
              groupKey,
              groupOrder: sectionSortOrder,
              groupColor: sectionColor,
              lookup,
            })
          })
          return
        }

        const baseKey = entry?.key || `field_${sectionIndex}`
        const labelObj = this.coerceAttachmentLabel(entry?.label)
        const legacySectionOrder = this.pickGroupSortOrder(entry, sectionIndex)
        const legacySectionColor = this.pickGroupColor(entry)

        if (labelObj && typeof labelObj === 'object' && !Array.isArray(labelObj)) {
          Object.entries(labelObj).forEach(([gt, labels], mapIdx) => {
            if (!Array.isArray(labels)) return
            labels.forEach((label, labelIndex) => {
              fields.push({
                key: `${baseKey}_${this.normalizeName(gt)}_${labelIndex}`,
                label,
                groupTitle: String(gt),
                groupKey: `${baseKey}_${this.normalizeName(gt)}`,
                groupOrder: legacySectionOrder * 1000 + mapIdx,
                groupColor: legacySectionColor,
              })
            })
          })
          return
        }

        fields.push({
          key: baseKey,
          label: labelObj || entry,
          groupTitle: 'عام',
          groupKey: `${baseKey}_general`,
          groupOrder: legacySectionOrder,
          groupColor: legacySectionColor,
        })
      })

      return fields
    },
    attachmentGroups() {
      const groupsMap = {}

      this.attachmentFields.forEach(field => {
        if (!groupsMap[field.groupKey]) {
          const parsed = field.groupOrder != null ? Number(field.groupOrder) : NaN
          const order = Number.isFinite(parsed) ? parsed : Number.MAX_SAFE_INTEGER
          groupsMap[field.groupKey] = {
            groupKey: field.groupKey,
            groupTitle: field.groupTitle || 'عام',
            order,
            color: field.groupColor ?? null,
            fields: [],
          }
        }
        groupsMap[field.groupKey].fields.push(field)
      })

      return Object.values(groupsMap).sort((a, b) => {
        if (a.order !== b.order) return a.order - b.order
        return String(a.groupKey).localeCompare(String(b.groupKey), 'ar')
      })
    },
  },
  watch: {
    resolvedExternalRequestId: {
      immediate: true,
      handler(externalRequestId) {
        this.init(externalRequestId)
      },
    },
    attachmentFields: {
      immediate: true,
      handler(fields) {
        fields.forEach(field => {
          if (!(field.key in this.fieldUploads)) {
            this.$set(this.fieldUploads, field.key, null)
          }
          if (!(field.key in this.uploadingFields)) {
            this.$set(this.uploadingFields, field.key, false)
          }
        })
      },
    },
    attachmentGroups: {
      immediate: true,
      handler(groups) {
        groups.forEach(group => {
          if (!(group.groupKey in this.attachmentGroupCollapse)) {
            this.$set(this.attachmentGroupCollapse, group.groupKey, false)
          }
        })
      },
    },
  },
  methods: {
    ...mapActions('attachmentLookups', ['serviceTypesAttachments']),
    init(externalRequestId = this.resolvedExternalRequestId) {
      if (this.lastLoadedServiceTypeId !== SERVICE_TYPE_ID) {
        this.lastLoadedServiceTypeId = SERVICE_TYPE_ID
        this.serviceTypesAttachments({
          service_type_id: SERVICE_TYPE_ID,
        }).catch(error => {
          console.log(error)
        })
      }

      if (externalRequestId) {
        this.fetchMedia(externalRequestId)
      }
    },
    async fetchMedia(externalRequestId = this.resolvedExternalRequestId) {
      if (!externalRequestId) return

      this.mediaLoading = true
      try {
        const response = await externalRequestsApi.media(externalRequestId)
        this.media = response?.data || {}
      } catch (error) {
        console.log(error)
        this.media = {}
      } finally {
        this.mediaLoading = false
      }
    },
    isUploadingConditional(key) {
      return !!(this.uploadingFields && this.uploadingFields[key])
    },
    isGroupExpanded(groupKey) {
      if (!(groupKey in this.attachmentGroupCollapse)) {
        this.$set(this.attachmentGroupCollapse, groupKey, false)
      }
      return !!this.attachmentGroupCollapse[groupKey]
    },
    toggleGroupCollapse(groupKey) {
      this.$set(this.attachmentGroupCollapse, groupKey, !this.attachmentGroupCollapse[groupKey])
    },
    canPreviewFile(file) {
      return !!(file && (file.is_image || file.is_video || file.is_document))
    },
    normalizeName(value) {
      return String(value || '')
        .trim()
        .replace(/\s+/g, ' ')
    },
    pickGroupSortOrder(entry, sectionIndex) {
      const dept = entry?.attachment_department
      const raw =
        entry?.order ??
        entry?.sort_order ??
        entry?.display_order ??
        entry?.group_order ??
        dept?.order ??
        dept?.sort_order ??
        dept?.display_order ??
        dept?.group_order
      const n = raw != null ? Number(raw) : NaN
      return Number.isFinite(n) ? n : sectionIndex
    },
    pickGroupColor(entry) {
      const dept = entry?.attachment_department
      const raw =
        entry?.color ??
        entry?.colour ??
        entry?.hex_color ??
        entry?.accent_color ??
        dept?.color ??
        dept?.colour ??
        dept?.hex_color ??
        dept?.accent_color
      if (raw == null || raw === '') return null
      return typeof raw === 'string' ? raw.trim() : String(raw).trim()
    },
    safeAttachmentGroupColor(color) {
      if (color == null || color === '') return null
      const t = typeof color === 'string' ? color.trim() : String(color).trim()
      if (/^#[0-9A-Fa-f]{3,8}$/.test(t)) return t
      if (/^[0-9A-Fa-f]{3}$/.test(t)) return `#${t}`
      if (/^[0-9A-Fa-f]{6}$/.test(t)) return `#${t}`
      if (/^rgba?\(\s*[\d.%\s,]+\s*\)$/i.test(t)) return t
      if (/^hsla?\(\s*[\d.%\s,]+\s*\)$/i.test(t)) return t
      return null
    },
    attachmentGroupCssVars(group) {
      const c = this.safeAttachmentGroupColor(group?.color)
      if (!c) return {}
      return { '--attachment-group-accent': c }
    },
    attachmentGroupAccentClass(group) {
      return this.safeAttachmentGroupColor(group?.color) ? 'attachment-field-group--accent' : ''
    },
    coerceAttachmentLabel(raw) {
      if (raw == null) return raw
      if (typeof raw === 'string') {
        const t = raw.trim()
        if (t.startsWith('{') && t.endsWith('}')) {
          try {
            return JSON.parse(t)
          } catch (e) {
            return raw
          }
        }
        return raw
      }
      return raw
    },
    pickUploadedFile(responseData, fallbackLabel) {
      if (!responseData) return { name: fallbackLabel }

      if (Array.isArray(responseData)) {
        return responseData[0] || { name: fallbackLabel }
      }

      if (Array.isArray(responseData?.data)) {
        return responseData.data[0] || { name: fallbackLabel }
      }

      if (responseData?.data && typeof responseData.data === 'object') {
        return responseData.data
      }

      if (typeof responseData === 'object') {
        return responseData
      }

      return { name: fallbackLabel }
    },
    async deleteConditionalFile(key, fileId) {
      const markDeleted = () => {
        if (fileId) this.$set(this.localDeletedFileIds, fileId, true)
        const remaining = this.getConditionalFiles(key)
        if (!remaining.length) {
          this.$delete(this.localUploadedFiles, key)
          this.$set(this.localDeletedFiles, key, true)
        }
        if (this.$bvToast) {
          this.$bvToast.toast('تم حذف الملف بنجاح', {
            title: 'المرفقات',
            variant: 'success',
            solid: true,
          })
        }
      }

      const doDelete = async () => {
        if (!fileId) {
          markDeleted()
          return
        }
        await this.$store.dispatch('media/delete', { id: fileId })
        markDeleted()
      }

      if (!this.$swal) {
        try {
          await doDelete()
        } catch (error) {
          if (this.$bvToast) {
            this.$bvToast.toast(error?.response?.data?.message || 'تعذر حذف الملف', {
              title: 'المرفقات',
              variant: 'danger',
              solid: true,
            })
          }
        }
        return
      }

      this.$swal({
        title: 'هل انت متاكد من حذف هذا الملف',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#06284D',
        cancelButtonColor: '#d33',
        confirmButtonText: 'نعم',
        cancelButtonText: 'لا',
      }).then(async result => {
        if (!result.isConfirmed) return
        try {
          await doDelete()
        } catch (error) {
          this.$swal({
            icon: 'error',
            title: error?.response?.data?.message || 'تعذر حذف الملف',
            showConfirmButton: true,
            confirmButtonColor: '#06284D',
            allowOutsideClick: true,
          })
        }
      })
    },
    async refreshMediaOnly() {
      await this.fetchMedia()
    },
    async handleFileActionRefresh(key) {
      this.$delete(this.localDeletedFiles, key)
      await this.refreshMediaOnly()
    },
    submitFieldUpload(field) {
      if (!this.resolvedExternalRequestId) return

      const files = this.isFieldMultiple(field)
        ? this.getSelectedFilesForField(field)
        : this.getSelectedFilesForField(field).slice(0, 1)
      const validationMessage = this.validateLookupFiles(field, files)
      if (validationMessage) {
        if (this.$swal) {
          this.$swal({
            icon: 'error',
            title: validationMessage,
            showConfirmButton: true,
            confirmButtonColor: '#06284D',
            allowOutsideClick: true,
          })
        } else if (this.$bvToast) {
          this.$bvToast.toast(validationMessage, {
            title: 'المرفقات',
            variant: 'danger',
            solid: true,
          })
        }
        return
      }

      this.$set(this.uploadingFields, field.key, true)
      const { formData, isMultiple } = this.buildFieldUploadFormData(field, files)
      const upload = isMultiple
        ? externalRequestsApi.uploadMultipleAttachments(this.resolvedExternalRequestId, formData)
        : externalRequestsApi.uploadAttachments(this.resolvedExternalRequestId, formData)

      upload
        .then(async response => {
          this.$set(this.fieldUploads, field.key, null)
          const uploadedFiles = this.pickUploadedFilesFromResponse(response?.data, field.label)
          this.$delete(this.localDeletedFiles, field.key)
          uploadedFiles.forEach(item => {
            if (item?.id) this.$delete(this.localDeletedFileIds, item.id)
          })
          this.$set(
            this.localUploadedFiles,
            field.key,
            isMultiple
              ? uploadedFiles
              : uploadedFiles[0]
                ? { ...uploadedFiles[0], name: uploadedFiles[0]?.name || field.label }
                : null,
          )
          await this.refreshMediaOnly()
          if (this.$bvToast) {
            this.$bvToast.toast('تم حفظ الملف بنجاح', {
              title: 'المرفقات',
              variant: 'success',
              solid: true,
            })
          }
        })
        .catch(error => {
          const message = this.lookupUploadErrorMessage(error)
          if (this.$swal) {
            this.$swal({
              icon: 'error',
              title: message,
              showConfirmButton: true,
              confirmButtonColor: '#06284D',
              allowOutsideClick: true,
            })
            return
          }
          if (this.$bvToast) {
            this.$bvToast.toast(message, {
              title: 'المرفقات',
              variant: 'danger',
              solid: true,
            })
          }
        })
        .finally(() => {
          this.$set(this.uploadingFields, field.key, false)
        })
    },
  },
  filters: {
    formatJalali(value) {
      if (!value) return ''
      const date = new Date(value)
      return date.toLocaleDateString('ar-EG')
    },
  },
}
</script>

<style scoped>
.required-block-cards {
  gap: 1px;
}

.media-card .file-title {
  min-height: 42px;
}

.attachment-field-group--accent {
  border-inline-start: 4px solid var(--attachment-group-accent);
  padding-inline-start: 0.5rem;
}

.attachment-field-group--accent .attachment-field-group__toggle {
  border-color: var(--attachment-group-accent) !important;
  color: var(--attachment-group-accent) !important;
  box-shadow: inset 0 0 0 1px var(--attachment-group-accent) !important;
  background-color: transparent !important;
}

.attachments-144-loader {
  min-height: 120px;
}
</style>
